<template>
  <Teleport to="body">
    <div
      v-if="loginDialog.visible"
      class="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
    >
      <div
        class="relative w-full max-w-[520px] max-h-[90dvh] overflow-x-hidden overflow-y-auto rounded-2xl border border-border bg-background shadow-2xl font-['Noto_Sans_SC',sans-serif]"
        @click.stop
      >
        <div
          class="absolute -top-24 -right-16 w-[280px] h-[280px] rounded-full blur-[80px] pointer-events-none opacity-40"
          :style="{ background: glowBackground }"
        />
        <button
          type="button"
          class="absolute top-3 right-3 z-10 w-8 h-8 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted flex items-center justify-center"
          :aria-label="t('closeLogin')"
          @click="close"
        >
          <X :size="16" />
        </button>

        <div ref="loginFormAreaRef" tabindex="-1" class="relative login-form-area min-w-0 px-6 pt-8 pb-6 outline-none">
          <div class="text-center mb-6">
            <p class="font-bold leading-[1.2] text-foreground text-[24px] tracking-[-0.5px] pr-6">{{ isRegisterMode ? t("registerTitle") : t("welcomeBack") }}</p>
            <p class="mt-2 font-normal leading-[22px] text-muted-foreground text-[14px]">{{ isRegisterMode ? t("registerSubtitle") : t("loginSubtitle") }}</p>
            <!-- 语言切换已隐藏，强制英文 -->
            <!--
            <div class="mt-3 flex items-center justify-center gap-1">
              <button
                v-for="item in LANGS"
                :key="item.key"
                type="button"
                data-login-enter-skip
                :class="[
                  'h-7 px-2.5 rounded-full text-[12px]',
                  locale === item.key ? 'bg-[#FF007F]/15 text-[#FF007F] font-semibold' : 'text-muted-foreground hover:text-foreground',
                ]"
                @click="setLocale(item.key)"
              >
                {{ item.label }}
              </button>
            </div>
            -->
          </div>

          <!-- 手机验证码登录已隐藏，仅保留账号密码登录 -->
          <!--
          <div class="w-full h-[52px] bg-muted rounded-[6px] p-[6px] flex items-stretch mb-6">
            <button type="button" data-login-enter-skip :class="tabClass('sms')" @click="loginMethod = 'sms'">
              <Smartphone :size="16" :stroke-width="1.5" class="shrink-0" />
              <span class="font-medium text-[14px] truncate">{{ t("phoneLogin") }}</span>
            </button>
            <button type="button" data-login-enter-skip :class="tabClass('password')" @click="loginMethod = 'password'">
              <User :size="16" :stroke-width="1.5" class="shrink-0" />
              <span class="font-medium text-[14px] truncate">{{ t("accountLogin") }}</span>
            </button>
          </div>

          <form v-if="loginMethod === 'sms'" class="flex flex-col gap-4" @submit.prevent="loginBySmsSubmit">
            <div
              class="relative w-full h-[50px] bg-muted/40 rounded-[6px] border border-transparent focus-within:border-[#FF007F] transition-colors overflow-hidden group"
            >
              <Smartphone
                :size="18"
                :stroke-width="1.5"
                class="absolute left-[12px] top-1/2 -translate-y-1/2 text-muted-foreground group-focus-within:text-[#FF007F] transition-colors"
              />
              <input
                v-model="smsForm.phone"
                type="tel"
                inputmode="numeric"
                maxlength="11"
                :placeholder="t('phonePlaceholder')"
                class="w-full h-full bg-transparent pl-[40px] pr-[16px] outline-none text-foreground text-[16px] placeholder:text-muted-foreground"
              />
            </div>
            <div
              class="relative w-full h-[50px] bg-muted/40 rounded-[6px] border border-transparent focus-within:border-[#FF007F] transition-colors overflow-hidden group flex items-center"
            >
              <KeySquare
                :size="18"
                :stroke-width="1.5"
                class="absolute left-[12px] top-1/2 -translate-y-1/2 text-muted-foreground group-focus-within:text-[#FF007F] transition-colors"
              />
              <input
                v-model="smsForm.code"
                type="text"
                inputmode="numeric"
                maxlength="6"
                :placeholder="t('codePlaceholder')"
                class="flex-1 min-w-0 h-full bg-transparent pl-[40px] pr-[16px] outline-none text-foreground text-[16px] tracking-[2px] placeholder:text-muted-foreground"
              />
              <button
                type="button"
                data-login-enter-skip
                class="shrink-0 h-[34px] mr-[8px] px-3 rounded-[5px] text-[13px] font-medium transition-colors"
                :class="
                  !isValidPhone || countdown > 0 || isSending
                    ? 'bg-muted text-muted-foreground/50 cursor-not-allowed'
                    : 'bg-[#FF007F]/15 text-[#FF66A8] hover:bg-[#FF007F]/25 cursor-pointer'
                "
                :disabled="!isValidPhone || countdown > 0 || isSending"
                @click="sendCode"
              >
                {{ countdown > 0 ? tf("resendIn", { n: countdown }) : isSending ? t("sending") : t("getCode") }}
              </button>
            </div>
            <p class="text-[12px] text-muted-foreground/70 leading-[18px]">{{ t("autoRegister") }}</p>
            <button :class="submitClass" type="submit" :disabled="isLogin">{{ t("loginOrRegister") }}</button>
          </form>
          -->

          <form v-if="!isRegisterMode" class="flex flex-col gap-4" @submit.prevent="loginByPasswordSubmit">
            <div
              class="relative w-full h-[50px] bg-muted/40 rounded-[6px] border border-transparent focus-within:border-[#FF007F] transition-colors overflow-hidden group"
            >
              <User
                :size="18"
                :stroke-width="1.5"
                class="absolute left-[12px] top-1/2 -translate-y-1/2 text-muted-foreground group-focus-within:text-[#FF007F] transition-colors"
              />
              <input
                v-model="passwordForm.account"
                type="text"
                :placeholder="t('accountPlaceholder')"
                class="w-full h-full bg-transparent pl-[40px] pr-[16px] outline-none text-foreground text-[16px] placeholder:text-muted-foreground"
              />
            </div>
            <div
              class="relative w-full h-[50px] bg-muted/40 rounded-[6px] border border-transparent focus-within:border-[#FF007F] transition-colors overflow-hidden group"
            >
              <Lock
                :size="18"
                :stroke-width="1.5"
                class="absolute left-[12px] top-1/2 -translate-y-1/2 text-muted-foreground group-focus-within:text-[#FF007F] transition-colors"
              />
              <input
                v-model="passwordForm.password"
                :type="showPassword ? 'text' : 'password'"
                :placeholder="t('passwordPlaceholder')"
                class="w-full h-full bg-transparent pl-[40px] pr-[40px] outline-none text-foreground text-[16px] placeholder:text-muted-foreground"
              />
              <button
                type="button"
                data-login-enter-skip
                class="absolute right-0 top-0 flex h-full w-10 items-center justify-center text-muted-foreground hover:text-foreground"
                :aria-label="showPassword ? t('hidePassword') : t('showPassword')"
                @click="showPassword = !showPassword"
              >
                <Eye v-if="showPassword" :size="18" :stroke-width="1.5" />
                <EyeOff v-else :size="18" :stroke-width="1.5" />
              </button>
            </div>
            <div class="flex items-center justify-between gap-2 flex-wrap">
              <label class="flex items-center gap-2 cursor-pointer group">
                <div
                  class="relative w-[12px] h-[12px] border border-solid border-border group-hover:border-foreground rounded-[2px] transition-colors overflow-hidden"
                >
                  <input v-model="rememberMe" type="checkbox" class="absolute opacity-0 w-full h-full cursor-pointer peer" />
                  <div class="w-[8px] h-[8px] m-[1px] bg-[#FF007F] rounded-[1px] hidden peer-checked:block" />
                </div>
                <span class="font-medium text-muted-foreground group-hover:text-foreground transition-colors text-[14px]">
                  {{ t("rememberMe") }}
                </span>
              </label>
              <button
                type="button"
                class="font-normal text-[#FF007F] hover:text-[#E00070] text-[14px] transition-colors"
                @click="forgotPasswordVisible = true"
              >
                {{ t("forgotPassword") }}
              </button>
            </div>
            <button :class="submitClass" type="submit" :disabled="!canSubmit || isLogin">{{ t("loginOrRegister") }}</button>
          </form>

          <!-- 邮箱注册（与全页登录页一致：邀请码 + 邮箱 + 验证码 + 密码 + 确认密码，成功即自动登录） -->
          <form v-else class="flex flex-col gap-4" @submit.prevent="registerSubmit">
            <!-- 邀请码（选填）：填了即绑定推荐官，后续成交 / 充值参与分佣；留空即普通注册、不计佣 -->
            <div
              class="relative w-full h-[50px] bg-muted/40 rounded-[6px] border border-transparent focus-within:border-[#FF007F] transition-colors overflow-hidden group"
            >
              <Ticket
                :size="18"
                :stroke-width="1.5"
                class="absolute left-[12px] top-1/2 -translate-y-1/2 text-muted-foreground group-focus-within:text-[#FF007F] transition-colors"
              />
              <input
                v-model="registerForm.inviteCode"
                type="text"
                maxlength="12"
                autocomplete="off"
                autocapitalize="characters"
                spellcheck="false"
                :placeholder="t('inviteCodePlaceholder')"
                class="w-full h-full bg-transparent pl-[40px] pr-[16px] outline-none text-foreground text-[16px] tracking-[2px] uppercase placeholder:text-muted-foreground placeholder:normal-case"
              />
            </div>
            <!-- 邮箱：格式不对时**当场**给红字提示 —— 否则按钮只是「变灰」，用户不知道差在哪 -->
            <div class="flex flex-col gap-1.5">
              <div
                class="relative w-full h-[50px] bg-muted/40 rounded-[6px] border transition-colors overflow-hidden group"
                :class="emailInvalid ? 'border-red-500/70' : 'border-transparent focus-within:border-[#FF007F]'"
              >
                <Mail
                  :size="18"
                  :stroke-width="1.5"
                  class="absolute left-[12px] top-1/2 -translate-y-1/2 text-muted-foreground group-focus-within:text-[#FF007F] transition-colors"
                />
                <input
                  v-model="registerForm.email"
                  type="email"
                  autocomplete="email"
                  :placeholder="t('emailPlaceholder')"
                  class="w-full h-full bg-transparent pl-[40px] pr-[16px] outline-none text-foreground text-[16px] placeholder:text-muted-foreground"
                />
              </div>
              <p v-if="emailInvalid" class="px-1 text-[12px] leading-[18px] text-red-500">
                {{ t("invalidEmail") }}
              </p>
            </div>
            <div
              class="relative w-full h-[50px] bg-muted/40 rounded-[6px] border border-transparent focus-within:border-[#FF007F] transition-colors overflow-hidden group flex items-center"
            >
              <KeySquare
                :size="18"
                :stroke-width="1.5"
                class="absolute left-[12px] top-1/2 -translate-y-1/2 text-muted-foreground group-focus-within:text-[#FF007F] transition-colors"
              />
              <input
                v-model="registerForm.code"
                type="text"
                inputmode="numeric"
                maxlength="6"
                :placeholder="t('emailCodePlaceholder')"
                class="flex-1 min-w-0 h-full bg-transparent pl-[40px] pr-[16px] outline-none text-foreground text-[16px] tracking-[2px] placeholder:tracking-normal placeholder:text-muted-foreground"
              />
              <button
                type="button"
                data-login-enter-skip
                class="shrink-0 h-[34px] mr-[8px] px-3 rounded-[5px] text-[13px] font-medium text-white transition-colors"
                :class="
                  !isValidEmail || emailCountdown > 0 || isSendingEmail
                    ? 'bg-[#FF007F]/40 cursor-not-allowed'
                    : 'bg-[#FF007F] hover:bg-[#E00070] active:bg-[#C40064] cursor-pointer'
                "
                :disabled="!isValidEmail || emailCountdown > 0 || isSendingEmail"
                @click="sendEmailCode"
              >
                {{ emailCountdown > 0 ? tf("resendIn", { n: emailCountdown }) : isSendingEmail ? t("sending") : t("getCode") }}
              </button>
            </div>
            <div
              class="relative w-full h-[50px] bg-muted/40 rounded-[6px] border border-transparent focus-within:border-[#FF007F] transition-colors overflow-hidden group"
            >
              <Lock
                :size="18"
                :stroke-width="1.5"
                class="absolute left-[12px] top-1/2 -translate-y-1/2 text-muted-foreground group-focus-within:text-[#FF007F] transition-colors"
              />
              <input
                v-model="registerForm.password"
                :type="showRegisterPassword ? 'text' : 'password'"
                autocomplete="new-password"
                :placeholder="t('registerPasswordPlaceholder')"
                class="w-full h-full bg-transparent pl-[40px] pr-[40px] outline-none text-foreground text-[16px] placeholder:text-muted-foreground"
              />
              <button
                type="button"
                data-login-enter-skip
                class="absolute right-0 top-0 flex h-full w-10 items-center justify-center text-muted-foreground hover:text-foreground"
                :aria-label="showRegisterPassword ? t('hidePassword') : t('showPassword')"
                @click="showRegisterPassword = !showRegisterPassword"
              >
                <Eye v-if="showRegisterPassword" :size="18" :stroke-width="1.5" />
                <EyeOff v-else :size="18" :stroke-width="1.5" />
              </button>
            </div>
            <!-- 确认密码：两次不一致时当场红字提示（一致性属于用户自己能负责的字段，故也进 canRegister） -->
            <div class="flex flex-col gap-1.5">
              <div
                class="relative w-full h-[50px] bg-muted/40 rounded-[6px] border transition-colors overflow-hidden group"
                :class="confirmMismatch ? 'border-red-500/70' : 'border-transparent focus-within:border-[#FF007F]'"
              >
                <Lock
                  :size="18"
                  :stroke-width="1.5"
                  class="absolute left-[12px] top-1/2 -translate-y-1/2 text-muted-foreground group-focus-within:text-[#FF007F] transition-colors"
                />
                <input
                  v-model="registerForm.confirmPassword"
                  :type="showRegisterPassword ? 'text' : 'password'"
                  autocomplete="new-password"
                  :placeholder="t('registerConfirmPasswordPlaceholder')"
                  class="w-full h-full bg-transparent pl-[40px] pr-[16px] outline-none text-foreground text-[16px] placeholder:text-muted-foreground"
                />
              </div>
              <p v-if="confirmMismatch" class="px-1 text-[12px] leading-[18px] text-red-500">
                {{ t("passwordMismatch") }}
              </p>
            </div>
            <p class="text-[12px] text-muted-foreground/70 leading-[18px]">{{ t("emailRegisterHint") }}</p>
            <button :class="registerSubmitClass" type="submit" :disabled="!canRegister || isRegistering">
              {{ isRegistering ? t("registering") : t("registerSubmit") }}
            </button>
          </form>

          <div class="mt-4 flex items-center justify-center gap-1 text-[14px] min-w-0">
            <span class="text-muted-foreground">{{ isRegisterMode ? t("haveAccount") : t("noAccount") }}</span>
            <button
              type="button"
              data-login-enter-skip
              class="font-medium text-[#FF007F] hover:text-[#E00070] transition-colors"
              @click="switchMode"
            >
              {{ isRegisterMode ? t("goLogin") : t("goRegister") }}
            </button>
          </div>
          <!-- 临时隐藏同意条款，海外网站不需要同意条款 -->
          <!-- <div class="flex justify-center mt-4 min-w-0" :class="{ 'animate-pulse': notAgreedShake }">
            <label class="flex items-start gap-2 cursor-pointer group min-w-0 max-w-full">
              <span
                role="checkbox"
                :aria-checked="agreed"
                tabindex="-1"
                :class="[
                  'relative w-[14px] h-[14px] rounded-[3px] border transition-colors shrink-0 cursor-pointer mt-0.5',
                  agreed ? 'border-[#FF007F] bg-[#FF007F]' : notAgreedShake ? 'border-red-400' : 'border-border',
                ]"
                @click="toggleAgreed"
              >
                <svg
                  v-if="agreed"
                  class="absolute inset-0 w-full h-full text-white p-[2px]"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="3.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </span>
              <span class="font-normal text-muted-foreground text-[12px] leading-[18px] text-left break-words">
                {{ t("agreePrefix") }}
                <a
                  class="text-[#FF007F] hover:underline hover:text-[#E00070] transition-colors cursor-pointer"
                  @click="policyModal = 'terms'"
                >
                  {{ t("terms") }}
                </a>
                {{ t("and") }}
                <a
                  class="text-[#FF007F] hover:underline hover:text-[#E00070] transition-colors cursor-pointer"
                  @click="policyModal = 'privacy'"
                >
                  {{ t("privacy") }}
                </a>
              </span>
            </label>
          </div> -->
        </div>
      </div>
    </div>
  </Teleport>

  <!-- 重置密码用户名默认带入登录弹窗账号 -->
  <!-- 原逻辑：:default-phone="isPhone(passwordForm.account) ? passwordForm.account.trim() : ''" -->
  <ForgotPasswordDialog v-model="forgotPasswordVisible" :default-username="passwordForm.account.trim()" />
  <PolicyDocumentModal v-model="policyModal" :show-agree="true" @agree="agreeAndClose" />
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, reactive, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { ElMessage } from "element-plus";
import { Eye, EyeOff, KeySquare, Lock, Mail, Ticket, User, X } from "@lucide/vue";
// 手机验证码登录已隐藏：import { KeySquare, Smartphone } from "@lucide/vue";
import type { LoginResult } from "@/@api/AuthApi";
import PolicyDocumentModal, { type PolicyDocumentType } from "@/components/PolicyDocumentModal.vue";
import { FEATURED_PATH } from "@/constants/appChrome";
import { useI18n } from "@/i18n/useI18n";
import { encryptPassword } from "@/service/passwordCrypto.js";
import { httpPost, ServerType } from "@/service/httpRequest";
import common from "@/service/common";
import { useLoginDialogStore } from "@/store/loginDialog";
// 语言切换已隐藏：import type { AppLocale } from "@/store/locale";
import { usePowerStore } from "@/store/power";
import { consumePendingInviteCode } from "@/utils/inviteCapture";
import { loadRememberedCredentials, saveRememberedCredentials, clearRememberedCredentials } from "@/utils/loginCredentialStorage";
import ForgotPasswordDialog from "@/views/login/components/ForgotPasswordDialog.vue";

// 手机号格式校验已取消：const PHONE_RE = /^1[3-9]\d{9}$/;
const glowBackground = "radial-gradient(circle, rgba(255,0,127,0.35), transparent 65%)";

const router = useRouter();
const route = useRoute();
const loginDialog = useLoginDialogStore();
const { t, tf } = useI18n();
// 手机验证码登录已隐藏：const { t, tf } = useI18n();
// 语言切换已隐藏，强制英文
// const { locale, setLocale } = useI18n();

/** 与原型登录弹窗一致：标题下三语胶囊切换 */
// const LANGS: { key: AppLocale; label: string }[] = [
//   { key: "en", label: "EN" },
//   { key: "zh", label: "中文" },
//   { key: "id", label: "ID" },
// ];

const loginFormAreaRef = ref<HTMLDivElement | null>(null);
// 手机验证码登录已隐藏，默认只走账号密码
// const loginMethod = ref<"sms" | "password">("sms");
// const smsForm = reactive({ phone: "", code: "" });
const passwordForm = reactive({ account: "", password: "" });
/** 密码是否明文显示 */
const showPassword = ref(false);
const isLogin = ref(false);
// const isSending = ref(false);
// const countdown = ref(0);
// let countdownTimer: ReturnType<typeof setInterval> | null = null;
const rememberMe = ref(true);
const agreed = ref(false);
// 用户协议勾选已隐藏，不再用未勾选抖动提示
// const notAgreedShake = ref(false);
const policyModal = ref<PolicyDocumentType | null>(null);
const forgotPasswordVisible = ref(false);

/** 弹窗内的「登录 / 邮箱注册」模式切换 */
const mode = ref<"login" | "register">("login");
const isRegisterMode = computed(() => mode.value === "register");
const registerForm = reactive({ inviteCode: "", email: "", code: "", password: "", confirmPassword: "" });
const showRegisterPassword = ref(false);
const isRegistering = ref(false);
const isSendingEmail = ref(false);
const emailCountdown = ref(0);
let emailCountdownTimer: ReturnType<typeof setInterval> | null = null;
const EMAIL_RE = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}$/;
const isValidEmail = computed(() => EMAIL_RE.test(registerForm.email.trim()));
/** 邮箱「填了但格式不对」——只提示、不清空，避免用户以为自己没输入 */
const emailInvalid = computed(
  () => registerForm.email.trim().length > 0 && !isValidEmail.value,
);
/** 两次密码都填了却不一致 */
const confirmMismatch = computed(
  () =>
    registerForm.confirmPassword.trim().length > 0 &&
    registerForm.confirmPassword.trim() !== registerForm.password.trim(),
);
/**
 * 注册按钮可点条件 —— **刻意不包含邮箱验证码**。
 *
 * <p>验证码是不是必填由**后端**决定（{@code email.code.verify-enabled}）：
 * 本地联调 / 演示环境会把它关掉（{@code EmailRegisterServiceImpl#registerByEmail} 里
 * 跳过校验但保留输入框）。前端若仍把「验证码非空」当成禁用条件，按钮就永远是灰的 ——
 * 开关传了等于没传，用户看到的现象是「注册按钮点不动」。</p>
 *
 * <p>因此这里只校验用户自己能负责的字段（邮箱格式 / 密码长度 / 两次一致），
 * 验证码的正确性统一交给后端判定：开校验时后端返回「验证码错误或已过期」，
 * 关校验时直接放行。</p>
 */
const canRegister = computed(
  () =>
    isValidEmail.value &&
    registerForm.password.trim().length >= 6 &&
    registerForm.password.trim().length <= 16 &&
    registerForm.confirmPassword.trim().length >= 6 &&
    registerForm.confirmPassword.trim() === registerForm.password.trim(),
);
const registerSubmitClass = computed(() =>
  [
    "w-full h-[50px] mt-3 rounded-[6px] transition-all flex items-center justify-center font-bold text-white text-[16px]",
    canRegister.value && !isRegistering.value
      ? "bg-[#FF007F] hover:bg-[#E00070] active:bg-[#C40064] cursor-pointer"
      : "bg-[#FF007F]/40 cursor-not-allowed",
  ].join(" "),
);

// const isValidPhone = computed(() => PHONE_RE.test(smsForm.phone.trim()));
// 手机号格式校验已取消：const isPhone = (value: string) => PHONE_RE.test(value.trim());

/** 用户名和密码都填写后，登录按钮才可点击 */
const canSubmit = computed(() => passwordForm.account.trim().length > 0 && passwordForm.password.trim().length > 0);

const submitClass = computed(() =>
  [
    "w-full h-[50px] mt-3 rounded-[6px] transition-all flex items-center justify-center font-bold text-white text-[16px]",
    canSubmit.value && !isLogin.value
      ? "bg-[#FF007F] hover:bg-[#E00070] active:bg-[#C40064] cursor-pointer"
      : "bg-[#FF007F]/40 cursor-not-allowed",
  ].join(" "),
);

/** 登录方式 Tab 样式（手机验证码登录隐藏后暂不使用） */
// const tabClass = (method: "sms" | "password"): string =>
//   [
//     "flex-1 min-w-0 flex items-center justify-center gap-1.5 rounded-[4px] transition-all",
//     loginMethod.value === method
//       ? "bg-background drop-shadow-[0px_1px_1.5px_rgba(0,0,0,0.1),0px_1px_1px_rgba(0,0,0,0.1)] text-foreground"
//       : "text-muted-foreground hover:text-foreground",
//   ].join(" ");

const close = (): void => {
  loginDialog.close();
};

/** 关闭登录弹窗后清空账号、密码和提交中状态，登录按钮回到不可点击 */
const resetLoginForm = (): void => {
  passwordForm.account = "";
  passwordForm.password = "";
  showPassword.value = false;
  isLogin.value = false;
  rememberMe.value = true;
  forgotPasswordVisible.value = false;
  mode.value = "login";
  registerForm.inviteCode = "";
  registerForm.email = "";
  registerForm.code = "";
  registerForm.password = "";
  registerForm.confirmPassword = "";
  showRegisterPassword.value = false;
  isRegistering.value = false;
  isSendingEmail.value = false;
  clearEmailCountdown();
  emailCountdown.value = 0;
};

/** 再次打开时回填已记住的账号密码；表单已有输入则不覆盖 */
const restoreRememberedCredentials = (): void => {
  void loadRememberedCredentials().then((saved) => {
    if (!loginDialog.visible) return;
    if (passwordForm.account || passwordForm.password) return;
    if (saved?.enabled && saved.account) {
      passwordForm.account = saved.account;
      passwordForm.password = saved.password;
      rememberMe.value = true;
    }
  });
};

/**
 * 扫码落地回填邀请码：用户从推荐官海报二维码进来（`/?invite=XXXXXX`），
 * 路由守卫已把邀请码暂存到 sessionStorage ⇒ 弹窗打开时切到注册表单并回填。
 *
 * <p>用后即焚（consumePendingInviteCode 内部 removeItem），避免下次手动打开登录框
 * 还残留上一个人的邀请码。已有输入不覆盖。</p>
 */
const applyPendingInviteCode = (): void => {
  const code = consumePendingInviteCode();
  if (!code) return;
  mode.value = "register";
  if (!registerForm.inviteCode) {
    registerForm.inviteCode = code;
  }
};

const agreeAndClose = (): void => {
  agreed.value = true;
  policyModal.value = null;
};

// const toggleAgreed = (event: MouseEvent): void => {
//   agreed.value = !agreed.value;
//   const el = event.currentTarget;
//   if (el instanceof HTMLElement) el.blur();
//   loginFormAreaRef.value?.focus();
// };

// const clearCountdown = (): void => {
//   if (countdownTimer) {
//     clearInterval(countdownTimer);
//     countdownTimer = null;
//   }
// };

// const startCountdown = (): void => {
//   clearCountdown();
//   countdown.value = 60;
//   countdownTimer = setInterval(() => {
//     countdown.value -= 1;
//     if (countdown.value <= 0) clearCountdown();
//   }, 1000);
// };

// 用户协议勾选已隐藏，登录不再校验是否同意条款
// const ensureAgreed = (): boolean => {
//   if (agreed.value) return true;
//   notAgreedShake.value = true;
//   ElMessage.warning(t("agreeTermsFirst"));
//   setTimeout(() => (notAgreedShake.value = false), 500);
//   return false;
// };

/** 发送短信验证码（手机验证码登录已隐藏） */
// const sendCode = (): void => {
//   const phone = smsForm.phone.trim();
//   if (!isPhone(phone)) {
//     ElMessage.warning(t("invalidPhone"));
//     return;
//   }
//   isSending.value = true;
//   httpPost(ServerType.Base, { url: "/ai-video/sms/send", data: { phone } })
//     .then(() => {
//       ElMessage.success(t("codeSent"));
//       startCountdown();
//     })
//     .finally(() => {
//       isSending.value = false;
//     });
// };

/** 整页刷新后补一次「登录成功」提示（刷新会清掉当前页的 Message） */
const LOGIN_SUCCESS_FLAG = "login_success";

/** 登录成功后提示、回跳或刷新当前页 */
const afterLoginSuccess = async (): Promise<void> => {
  const redirectPath = loginDialog.redirectPath;
  const sessionExpired = loginDialog.sessionExpired;
  loginDialog.close();
  if (sessionExpired) {
    sessionStorage.setItem(LOGIN_SUCCESS_FLAG, "1");
    window.location.href = redirectPath || route.fullPath || FEATURED_PATH;
    return;
  }
  ElMessage.success(t("loginSuccess"));
  const token = common.getToken();
  const signKey = common.getSignKey();
  if (token && signKey) {
    await common.getUserInfo(token, signKey);
    await usePowerStore().init();
  }
  if (redirectPath && redirectPath !== route.fullPath) {
    await router.push(redirectPath);
  }
};

// const loginBySms = (phone: string, code: string) => {
//   return httpPost<LoginResult>(ServerType.Base, {
//     url: "/ai-video/sms/verify",
//     data: { phone, code },
//   }).then(async (data) => {
//     if (data.token) {
//       common.logIn(data.token, data.signKey);
//       await afterLoginSuccess();
//     }
//   });
// };

const loginByPassword = async (username: string, password: string, remember: boolean) => {
  const encryptedPassword = await encryptPassword(password);
  return httpPost<LoginResult>(ServerType.Base, {
    url: "/auth/email/user-login",
    data: { username, password: encryptedPassword, rememberMe: remember },
  }).then(async (data) => {
    if (data.token) {
      if (remember) {
        await saveRememberedCredentials(username, password);
      } else {
        clearRememberedCredentials();
      }
      common.logIn(data.token, data.signKey);
      await afterLoginSuccess();
    }
  });
};

// const loginBySmsSubmit = async (): Promise<void> => {
//   if (!ensureAgreed()) return;
//   const phone = smsForm.phone.trim();
//   const code = smsForm.code.trim();
//   if (!phone) {
//     ElMessage.warning(t("enterPhone"));
//     return;
//   }
//   if (!isPhone(phone)) {
//     ElMessage.warning(t("invalidPhone"));
//     return;
//   }
//   if (!code) {
//     ElMessage.warning(t("enterCode"));
//     return;
//   }
//   isLogin.value = true;
//   try {
//     await loginBySms(phone, code);
//   } finally {
//     isLogin.value = false;
//   }
// };

const loginByPasswordSubmit = async (): Promise<void> => {
  const account = passwordForm.account.trim();
  const password = passwordForm.password.trim();
  if (!account) {
    ElMessage.warning(t("enterAccountOrPhone"));
    return;
  }
  if (!password) {
    ElMessage.warning(t("enterPassword"));
    return;
  }
  isLogin.value = true;
  try {
    await loginByPassword(account, password, rememberMe.value);
  } finally {
    isLogin.value = false;
  }
};

/** 邮箱验证码倒计时 */
const clearEmailCountdown = (): void => {
  if (emailCountdownTimer) {
    clearInterval(emailCountdownTimer);
    emailCountdownTimer = null;
  }
};

const startEmailCountdown = (): void => {
  clearEmailCountdown();
  emailCountdown.value = 60;
  emailCountdownTimer = setInterval(() => {
    emailCountdown.value -= 1;
    if (emailCountdown.value <= 0) clearEmailCountdown();
  }, 1000);
};

/** 发送邮箱注册验证码 */
const sendEmailCode = async (): Promise<void> => {
  const email = registerForm.email.trim();
  if (!email) {
    ElMessage.warning(t("enterEmail"));
    return;
  }
  if (!isValidEmail.value) {
    ElMessage.warning(t("invalidEmail"));
    return;
  }
  isSendingEmail.value = true;
  try {
    await httpPost(ServerType.Base, { url: "/auth/email/send-code", data: { email } });
    ElMessage.success(t("emailCodeSent"));
    startEmailCountdown();
  } finally {
    isSendingEmail.value = false;
  }
};

/** 邮箱注册并自动登录 */
const registerSubmit = async (): Promise<void> => {
  const email = registerForm.email.trim();
  const code = registerForm.code.trim();
  const password = registerForm.password.trim();
  const confirmPassword = registerForm.confirmPassword.trim();
  if (!email) {
    ElMessage.warning(t("enterEmail"));
    return;
  }
  if (!isValidEmail.value) {
    ElMessage.warning(t("invalidEmail"));
    return;
  }
  // 验证码是否必填由后端开关决定（见 canRegister 注释）：这里**不再拦截空值**，
  // 否则后端关掉校验后前端仍会弹「请输入验证码」，用户填不了码就永远注册不了。
  if (!password) {
    ElMessage.warning(t("enterPassword"));
    return;
  }
  if (password.length < 6 || password.length > 16) {
    ElMessage.warning(t("passwordLen616"));
    return;
  }
  if (!confirmPassword) {
    ElMessage.warning(t("enterConfirmPassword"));
    return;
  }
  if (password !== confirmPassword) {
    ElMessage.warning(t("passwordMismatch"));
    return;
  }
  isRegistering.value = true;
  try {
    const [encryptedPassword, encryptedConfirmPassword] = await Promise.all([
      encryptPassword(password),
      encryptPassword(confirmPassword),
    ]);
    const data = await httpPost<LoginResult>(ServerType.Base, {
      url: "/auth/email/register",
      data: {
        email,
        code,
        password: encryptedPassword,
        confirmPassword: encryptedConfirmPassword,
        // 邀请码选填：归一化后仍为空则不带该字段，后端按「普通注册」处理
        inviteCode: registerForm.inviteCode.trim().toUpperCase() || undefined,
      },
    });
    if (data.token) {
      common.logIn(data.token, data.signKey);
      await afterLoginSuccess();
    }
  } finally {
    isRegistering.value = false;
  }
};

/** 登录 / 注册互切 */
const switchMode = (): void => {
  mode.value = isRegisterMode.value ? "login" : "register";
};

const shouldSkipLoginEnter = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) return false;
  if (target.closest("[data-login-enter-skip]")) return true;
  if (target instanceof HTMLInputElement && target.closest("form")) return true;
  if (target instanceof HTMLButtonElement && target.type === "submit") return true;
  return target.tagName === "TEXTAREA";
};

const handleGlobalLoginEnter = (event: KeyboardEvent): void => {
  if (!loginDialog.visible || event.key !== "Enter" || event.isComposing || event.defaultPrevented) return;
  if (policyModal.value || forgotPasswordVisible.value) return;
  if (shouldSkipLoginEnter(event.target)) return;
  event.preventDefault();
  if (isRegisterMode.value) {
    void registerSubmit();
  } else {
    void loginByPasswordSubmit();
  }
};

watch(
  () => loginDialog.visible,
  (visible) => {
    if (!visible) {
      resetLoginForm();
      return;
    }
    restoreRememberedCredentials();
    // 扫码落地：先于「记住的账号」展示逻辑也没关系——注册表单与登录表单互斥展示
    applyPendingInviteCode();
    if (sessionStorage.getItem("session_expired") === "1") {
      sessionStorage.removeItem("session_expired");
      ElMessage.warning(t("sessionExpired"));
    }
    nextTick(() => loginFormAreaRef.value?.focus());
  },
);

onMounted(() => {
  document.addEventListener("keydown", handleGlobalLoginEnter);
  if (sessionStorage.getItem(LOGIN_SUCCESS_FLAG) === "1") {
    sessionStorage.removeItem(LOGIN_SUCCESS_FLAG);
    ElMessage.success(t("loginSuccess"));
  }
  if (loginDialog.visible) {
    restoreRememberedCredentials();
    applyPendingInviteCode();
  }
});

onUnmounted(() => {
  document.removeEventListener("keydown", handleGlobalLoginEnter);
  clearEmailCountdown();
});
</script>
